import Konva from "konva";
import * as ConnectorUtils from "frontend/canvas-designer-new/elements/connector/connector-utils";
import { Point } from "./math-utils";

/**
 * Updates frame attachments for elements based on their spatial position.
 * Used after elements are moved to ensure they're attached to the correct frames.
 */
export function updateFrameAttachments(
  movedElementIds: string[],
  frames: Record<string, string[]>,
  hiddenFrameIds: string[],
  layerRef: React.RefObject<Konva.Layer>,
  stageRef: React.RefObject<Konva.Stage>,
  getSelectableNodes: (filter: (node: Konva.Node) => boolean) => Konva.Node[],
  syncService: any
): void {
  if (movedElementIds.length === 0 || Object.keys(frames).length === 0) return;

  const stage = stageRef.current;
  if (!stage) return;

  // Get all visible frame nodes
  const frameNodes = getSelectableNodes((node) => node.attrs.isFrame && hiddenFrameIds.indexOf(node.attrs.id) === -1);

  // Track elements that need frameId updates
  const elementsToAttach = new Map<string, string[]>(); // frameId -> elementIds[]
  const elementsToDetach: string[] = [];

  // Process each moved element (excluding frames and frame titles)
  movedElementIds.forEach((elementId) => {
    const node = layerRef.current?.findOne(
      (n: Konva.Node) => n.attrs.id === elementId && !n.attrs.isFrame && !n.attrs.isFrameTitle
    );

    if (!node || !node.attrs.isSelectable) return;

    const currentFrameId = node.attrs.element.frameId;

    // Get element's center point for intersection testing
    const { x, y, width, height } = node.getClientRect({ relativeTo: stage as any });
    const centerRect = {
      x: x + width / 2,
      y: y + height / 2,
      width: 1,
      height: 1,
    };

    // Find the smallest frame that contains this element
    const intersectingFrame = frameNodes
      .filter((frame: any) => Konva.Util.haveIntersection(frame.getClientRect({ relativeTo: stage }), centerRect))
      .reduce((smallestFrame: any | null, frame: any) => {
        if (!smallestFrame) return frame;

        const { x: x1, y: y1, width: width1, height: height1 } = frame.getClientRect({ relativeTo: stage });
        const { x: x2, y: y2, width: width2, height: height2 } = smallestFrame.getClientRect({ relativeTo: stage });

        // Return the frame that is completely contained within the other
        if (x1 > x2 && x1 + width1 < x2 + width2 && y1 > y2 && y1 + height1 < y2 + height2) {
          return frame;
        }
        return smallestFrame;
      }, null);

    const newFrameId = intersectingFrame ? intersectingFrame.attrs.id : null;

    // Check if frameId needs to be updated
    if (currentFrameId !== newFrameId) {
      if (newFrameId) {
        // Element should be attached to a frame
        const elementsInFrame = elementsToAttach.get(newFrameId) || [];
        elementsInFrame.push(elementId);
        elementsToAttach.set(newFrameId, elementsInFrame);
      } else {
        // Element should be detached from its current frame
        elementsToDetach.push(elementId);
      }
    }
  });

  // Execute the frame attachment updates
  if (elementsToAttach.size > 0) {
    elementsToAttach.forEach((elementIds, frameId) => {
      syncService?.mutate.attachToFrame({ ids: elementIds, frameId });
    });
  }

  // Execute the frame detachment updates
  if (elementsToDetach.length > 0) {
    syncService?.mutate.attachToFrame({ ids: elementsToDetach, frameId: null });
  }
}

/**
 * Updates frame attachment for a single connector based on its anchor points.
 * Attaches connector to a frame if both anchor points are within the same frame.
 */
export function updateSingleConnectorFrameAttachment(
  connectorId: string,
  hiddenFrameIds: string[],
  layerRef: React.RefObject<Konva.Layer>,
  stageRef: React.RefObject<Konva.Stage>,
  getSelectableNodes: (filter: (node: Konva.Node) => boolean) => Konva.Node[],
  syncService: any
): void {
  const stage = stageRef.current;
  if (!stage) return;

  const connectorNode = layerRef.current?.findOne(`#${connectorId}`);
  if (!connectorNode || !connectorNode.attrs.element) return;

  const connector = connectorNode.attrs.element;
  const currentFrameId = connector.frameId;

  // Get connector anchor points from the points array
  const points = connector.points;
  if (!points || points.length < 2) return;

  const p1 = points[0];
  const p2 = points[1];

  let startPoint: Point;
  let endPoint: Point;
  try {
    startPoint = ConnectorUtils.elementPointToCanvasPoint(connector, p1);
    endPoint = ConnectorUtils.elementPointToCanvasPoint(connector, p2);
  } catch {
    return;
  }

  // Get all visible frame nodes
  const frameNodes = getSelectableNodes((node) => node.attrs.isFrame && hiddenFrameIds.indexOf(node.attrs.id) === -1);

  // Find frames that contain both anchor points
  const framesContainingBothAnchors = frameNodes.filter((frameNode: any) => {
    const frameRect = frameNode.getClientRect({ relativeTo: stage });

    const startInFrame =
      startPoint.x >= frameRect.x &&
      startPoint.x <= frameRect.x + frameRect.width &&
      startPoint.y >= frameRect.y &&
      startPoint.y <= frameRect.y + frameRect.height;

    const endInFrame =
      endPoint.x >= frameRect.x &&
      endPoint.x <= frameRect.x + frameRect.width &&
      endPoint.y >= frameRect.y &&
      endPoint.y <= frameRect.y + frameRect.height;

    return startInFrame && endInFrame;
  });

  // Find the smallest frame that contains both anchors
  const targetFrame = framesContainingBothAnchors.reduce((smallest: any | null, frame: any) => {
    if (!smallest) return frame;

    const frameRect = frame.getClientRect({ relativeTo: stage });
    const smallestRect = smallest.getClientRect({ relativeTo: stage });

    // Return the frame with smaller area
    const frameArea = frameRect.width * frameRect.height;
    const smallestArea = smallestRect.width * smallestRect.height;

    return frameArea < smallestArea ? frame : smallest;
  }, null);

  const newFrameId = targetFrame ? targetFrame.attrs.id : null;

  // Update frameId if it changed
  if (currentFrameId !== newFrameId) {
    syncService?.mutate.attachToFrame({ ids: [connectorId], frameId: newFrameId });
  }
}
